import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { create as createModal, useModal } from "@ebay/nice-modal-react";
import { AxiosError } from "axios";
import { CrossFadeSwitch } from "components/cross-fade-switch";
import { Form } from "components/input/form";
import { LoginError } from "errors/login-error";
import {
  StateMachineSteps,
  useMultiStepWizard,
} from "hooks/use-multi-step-wizard";
import { axios } from "lib/axios";
import { Bugsnag } from "lib/bugsnag";
import { FormError } from "patterns/form-error";
import { useForm } from "react-hook-form";
import { apiActions } from "screens/login/hooks/use-login-wizard";
import { DownloadAuthenticatorApp } from "screens/profile/components/download-authenticator-app";
import { ScanQrCode } from "screens/profile/components/scan-qr-code";
import { Steps } from "screens/profile/components/steps";
import { VerifyPassword } from "screens/profile/components/verify-password";
import { useProfile, User } from "screens/profile/hooks/use-profile";

type State = {
  password_confirmation: string;
  code: string;
  enrolment_id: string;
  sessionEnded?: boolean;
};

// The login session is gone, so retrying here can't work
class SessionEndedError extends Error {}

type EnableOutcome = "enabled" | "not_enabled" | "session_ended" | "unknown";

// A failed enable request isn't a failed enable: the reply can be lost after
// the server has switched the method on and re-issued the session cookie
async function getEnableOutcome(enrolmentId: string): Promise<EnableOutcome> {
  try {
    const {
      data: { result },
    } = await axios.get<{ result: User }>("user");
    const enrolment = result.multi_factor_enrolments.find(
      (e) => e.id === enrolmentId
    );
    return enrolment?.is_enabled ? "enabled" : "not_enabled";
  } catch (e) {
    if (!(e instanceof AxiosError)) return "unknown";
    // Only an enabled method makes the API ask for a code
    if (e.response?.data?.exception === "MultiFactorException") return "enabled";
    if (e.response?.status === 401) return "session_ended";
    return "unknown";
  }
}

export const MultiFactorEnrolment = createModal<{
  initialStepKey?: string;
  onEnrolmentComplete?: () => void;
}>(({ initialStepKey, onEnrolmentComplete }) => {
  const modal = useModal();
  const {
    createMultiFactorEnrolment,
    enableMultiFactorEnrolment,
    error: profileError,
    multiFactorEnrolment,
    getUser,
    setError: setProfileError,
  } = useProfile();
  const toast = useToast();

  async function recoverFromFailedEnable(
    error: Error,
    enrolmentId: string
  ): Promise<true> {
    // Replaced by the outcome below, or by success
    setProfileError(undefined);

    // The enable never ran: the session had gone before Activate was pressed
    if (error instanceof AxiosError && error.response?.status === 401) {
      throw new SessionEndedError(
        "Your session has expired. Please log in again to finish setting up multi-factor."
      );
    }

    Bugsnag.notify(error);

    const outcome = await getEnableOutcome(enrolmentId);
    if (outcome === "enabled") return true;
    if (outcome === "session_ended") {
      throw new SessionEndedError(
        "Your session ended while multi-factor was being switched on. Please log in again. You may be asked for a code from your authenticator app."
      );
    }
    if (outcome === "not_enabled") {
      throw new LoginError(
        "Multi-factor couldn't be switched on. Please try again."
      );
    }
    throw new LoginError(
      "We couldn't confirm whether multi-factor was switched on. Please try again."
    );
  }

  const steps = {
    downloadApp: {
      Component: DownloadAuthenticatorApp,
      title: "Download App",
      onSubmit: async ({ state }) => {
        if (!multiFactorEnrolment) {
          await createMultiFactorEnrolment();
        }
        return state;
      },
      getNextStep: () =>
        multiFactorEnrolment && !multiFactorEnrolment.is_enabled
          ? "verifyPassword"
          : "scanQRCode",
    },
    scanQRCode: {
      Component: ScanQrCode,
      title: "Scan QR Code",
      onSubmit: async ({ data, state }) => {
        const { isMultiFactorVerified } = await apiActions.verifyMultiFactor({
          data: { ...data, enrolment_id: multiFactorEnrolment?.id },
          state: state as any,
        });

        if (isMultiFactorVerified) {
          getUser();
        }
      },
      getNextStep: () => "verifyPassword",
    },
    verifyPassword: {
      Component: VerifyPassword,
      title: "Verify Password",
      onSubmit: async ({
        data,
      }: {
        data: { password_confirmation: string };
      }) => {
        const enrolmentId = multiFactorEnrolment?.id as string;
        let result: unknown;
        try {
          result = await enableMultiFactorEnrolment({
            id: enrolmentId,
            password_confirmation: data.password_confirmation as string,
          });
        } catch (e) {
          result = await recoverFromFailedEnable(e as Error, enrolmentId);
        }

        if (!result) return;

        // The method is on by now, so the refresh is best effort: a failed
        // one mustn't turn a completed setup back into an error
        await getUser().catch(() => setProfileError(undefined));

        toast({
          title: "Multi-factor authentication enabled",
          description:
            "All app sessions have been cleared and you will need to login again.",
          position: "top",
          status: "success",
          size: "xl",
          duration: 6000,
        });

        if (result) {
          modal.hide().then(() => modal.remove());
          onEnrolmentComplete?.();
        }
      },
      getNextStep: () => null,
    },
  } as const satisfies StateMachineSteps<State>;

  const wizard = useMultiStepWizard({
    steps,
    initialState: {} as State,
    initialStepKey: initialStepKey as keyof typeof steps,
    onError: function handleError(
      e: Error,
      { setError, state }
    ): { state: State; error: string | undefined } {
      if (e instanceof SessionEndedError) {
        setError(e.message);
        return { state: { ...state, sessionEnded: true }, error: e.message };
      }

      let error;
      if (e instanceof LoginError) {
        error = e.message;
      } else if (
        e instanceof AxiosError &&
        e?.response?.data?.exception === "ValidationException"
      ) {
        error = e?.response?.data?.message;
      } else {
        Bugsnag.notify(e);
        console.error(e);
        error = "An unknown error occurred";
      }

      setError(error);

      return { state, error };
    },
  });
  const { currentStep, error, stepHistory } = wizard;

  const form = useForm<State>({
    defaultValues: {
      enrolment_id: multiFactorEnrolment?.id,
      password_confirmation: "",
      code: "",
    },
  });

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Modal
      isOpen={modal.visible}
      onClose={modal.hide}
      onCloseComplete={modal.remove}
      size={["full", "lg"]}
      isCentered={useBreakpointValue({ base: false, md: false, lg: true })}
    >
      <Form form={form} onSubmit={currentStep.onSubmit}>
        <ModalOverlay bg={"blackAlpha.500"} />
        <ModalContent mt={0} mb={0}>
          <ModalHeader w={["90%", "auto"]}>
            Multi-factor authentication setup
          </ModalHeader>
          <ModalCloseButton size={"lg"} />
          <ModalBody>
            {initialStepKey !== "verifyPassword" && !isMobile ? (
              <Steps
                steps={steps}
                currentStep={currentStep.key}
                stepHistory={stepHistory}
              />
            ) : null}
            <FormError error={error || profileError} />
            <CrossFadeSwitch childKey={currentStep.key}>
              {currentStep ? <currentStep.Component {...wizard} /> : null}
            </CrossFadeSwitch>
          </ModalBody>
        </ModalContent>
      </Form>
    </Modal>
  );
});
